<template>
    <div
        :class="[
            'date-block',
            { [`date-block--size-${size}`]: size },
            { 'date-block--interval': dateEnd }
        ]"
    >
        <z-icon class="date-block__icon" name="sg-element" :width="iconSize" :height="iconSize" />
        <div class="date-block__before" v-if="!!this.$slots['before']">
            <slot name="before"/>
        </div>
        <div class="date-block__container" v-if="momentFormatIn && momentFormatOut">
            <div class="date-block__date">
                <div class="date-block__date">{{ buildDateString(date) }}</div>

                <span class="divider" v-if="dateEnd">&ndash;</span>
                <div class="date-block__date" v-if="dateEnd">{{ buildDateString(dateEnd) }}</div>
            </div>
        </div>
        <div class="date-block__container" v-else>
            <template v-if="size !== 'xl'">
                <div class="date-block__date">
                    <div class="date-block__date-wrapper">
                        <div class="date-block__date">{{ date | momentFilter('DD') }}</div>
                        <div class="date-block__ym-wrapper">
                            <span class="date-block__month">
                                {{ date | momentFilter('MMMM') }}
                            </span>
                            <span class="date-block__year">
                                {{ date | momentFilter('YYYY') }}
                            </span>
                        </div>
                    </div>
                    <template v-if="dateEnd && !dateEndInCurrentMonthAndYear">
                        <span class="divider">&ndash;</span>
                        <div class="date-block__date-wrapper">
                            <div class="date-block__date">
                                {{ dateEnd | momentFilter('DD') }}
                            </div>

                            <div class="date-block__ym-wrapper">
                                <span class="date-block__month date-block__month--end">
                                    {{ dateEnd | momentFilter('MMMM') }}</span>
                                <span class="date-block__year date-block__year--end">
                                    {{ dateEnd | momentFilter('YYYY') }}</span>
                            </div>
                        </div>
                    </template>
                </div>
            </template>

            <template v-else>
                <div class="date-block__date">
                    <div class="date-block__date">{{ date | momentFilter('DD') }}</div>
                    <template v-if="dateEnd && !isSameDate">
                        <span class="divider">&ndash;</span>
                        <div class="date-block__date">
                            {{ dateEnd | momentFilter('DD') }}
                        </div>
                    </template>
                </div>
                <div class="date-block__ym-wrapper">
                    <div class="date-block__ym-column">
                        <span class="date-block__month">
                            {{ date | momentFilter('MMMM') }}
                        </span>
                        <span class="date-block__year">
                            {{ date | momentFilter('YYYY') }}
                        </span>
                    </div>
                    <div class="date-block__ym-column date-block__ym-column--end" v-if="dateEnd && !dateEndInCurrentMonthAndYear">
                        <span class="divider"></span>
                        <span class="date-block__month date-block__month--end">
                            {{ dateEnd | momentFilter('MMMM') }}
                        </span>
                        <span class="date-block__year date-block__year--end">
                            {{ dateEnd | momentFilter('YYYY') }}
                        </span>
                    </div>
                </div>
            </template>
        </div>
        <div class="date-block__after" v-if="!!this.$slots['after']">
            <slot name="after"/>
        </div>
    </div>
</template>

<script>
import moment from 'moment'

export default {
    name: 'date-block',
    props: {
        date: [String, Number],
        dateEnd: [String, Number],
        size: {
            type: String,
            default: 'm',
            validator: (prop) => ['xl', 'l', 'm', 's'].includes(prop)
        },
        momentFormatIn: String,
        momentFormatOut: String
    },
    methods: {
        buildDateString (date) {
            return moment(date, this.momentFormatIn).format(this.momentFormatOut)
        }
    },
    computed: {
        iconSize () {
            switch (this.size) {
            case 'xl':
                return '82'
            case 'l':
                return '53'
            case 'm':
                return '34'
            case 's':
                return '32'
            default:
                return '34'
            }
        },
        isSameDate () {
            return this.$options.filters.momentFilter(this.date, 'DD') === this.$options.filters.momentFilter(this.dateEnd, 'DD')
        },
        dateEndInCurrentMonthAndYear () {
            return (
                moment.unix(this.date).format('YYYY') === moment.unix(this.dateEnd).format('YYYY') &&
                moment.unix(this.date).format('MMMM') === moment.unix(this.dateEnd).format('MMMM')
            )
        }
    },
    data () {
        return {
            lang: this.$root.lang
        }
    },
    created () {
        moment.locale(this.$root.lang, {
            months:
                this.$root.lang === 'ru'
                    ? [
                        'января',
                        'февраля',
                        'марта',
                        'апреля',
                        'мая',
                        'июня',
                        'июля',
                        'августа',
                        'сентября',
                        'октября',
                        'ноября',
                        'декабря'
                    ]
                    : [
                        'January',
                        'February',
                        'March',
                        'April',
                        'May',
                        'June',
                        'July',
                        'August',
                        'September',
                        'October',
                        'November',
                        'December'
                    ]
        })
    }
}
</script>

<style lang="scss">
.date-block {
    $parent: &;

    display: flex;
    align-items: center;

    @include breakpoint (mobile) {
        align-items: flex-start;
    }

    .divider {
        margin: 0 16px;
    }

    &__before,
    &__after {
        align-items: end;
    }

    &__before {
        margin-right: $token-spacers-3-xs;
    }

    &__after {
        margin-left: $token-spacers-3-xs;
    }

    &__container {
        display: flex;
        color: $token-colors-black-90;
        flex: 0 1 auto;
    }

    &__ym-wrapper {
        display: flex;
    }

    &__ym-column {
        display: flex;
        flex-direction: column;
        min-width: 77px;

        &--end {
            margin-left: 69px;

            @include breakpoint (mobile) {
                    margin-left: 44px;
            }
        }
    }

    &__date {
        z-index: 2;
        align-items: flex-start;

        &-wrapper {
            display: flex;

            @include breakpoint (mobile) {
                flex-direction: column;
            }
        }
    }

    &__date,
    &__date--end {
        display: flex;
    }

    &__month,
    &__year {
        font-weight: 500;
    }

    &--size {
        &-xl {
            &.date-block {
                align-items: flex-start !important;
            }

            #{$parent}__month {
                margin-right: 10px;
            }

            #{$parent}__icon {
                margin-top: 45px;
                margin-right: 24px;

                @include breakpoint (laptop) {
                    width: 62px !important;
                    height: 62px !important;
                    margin-top: 20px;
                    margin-right: 0;
                }

                @include breakpoint (mobile) {
                    width: 32px !important;
                    height: 32px !important;
                    margin-top: 0;
                    margin-right: 10px;
                }
            }

            #{$parent}__date {
                font-size: 160px;
                line-height: 1 !important;
                font-weight: 900 !important;

                @include breakpoint(laptop) {
                    @include typo-level(4XL);
                }

            }

            #{$parent}__container {
                flex-direction: column;
            }

            #{$parent}__ym-wrapper {
                @include typo-level(M);
                font-weight: 600;
                line-height: 120%;

            }
        }

        &-l {
            #{$parent}__icon {
                margin-right: 11px;
            }

            #{$parent}__date {
                font-weight: 600;
                font-size: 78px;
                line-height: 120%;
            }

            #{$parent}__container {
                flex-direction: row;
            }

            #{$parent}__ym-wrapper {
                flex-direction: column;
                justify-content: center;
                font-weight: 400;
                font-size: 24px;
                line-height: 120%;
                margin-left: 8px !important;
            }
        }

        &-m {
            #{$parent}__icon {
                margin-right: 11px;

                @include breakpoint(v-tablet) {
                    margin-right: 4px;
                    width: 26px !important;
                    height: 26px !important;
                }

                @include breakpoint(mobile) {
                    width: 22px !important;
                    height: 22px !important;
                    margin-top: 0.6em;
                }
            }

            #{$parent}__date,
            #{$parent}__before,
            #{$parent}__after {
                @include typo-level(2XL);
                font-weight: 600;
            }

            #{$parent}__date {
                @include breakpoint (v-tablet) {
                    font-size: 40px;
                }
            }

            #{$parent}__container {
                flex-direction: row;
                align-items: center;
            }

            #{$parent}__ym-wrapper {
                flex-direction: column;
                font-weight: 400;
                font-size: 18px;
                line-height: 1.2;
                margin-left: 12px;

                @include breakpoint(v-tablet) {
                    font-size: 16px;
                }

                @include breakpoint(mobile) {
                    margin-left: 0;
                }
            }
        }

        &-s {
            align-items: center;

            #{$parent}__icon {
                margin-top: 0;
            }

            #{$parent}__date,
            #{$parent}__month,
            #{$parent}__month--end {
                margin-right: 5px !important;
            }

            #{$parent}__date,
            #{$parent}__ym-wrapper {
                font-weight: 500;
                font-size: 16px;
                line-height: 140%;
                color: #2e2e34;

                @include breakpoint(mobile) {
                    font-size: 14px;
                }
            }

            #{$parent}__container {
                position: relative;
                flex-direction: row;
                background-color: #f2f2f6;
                padding: 5px 25px 5px 0;
                margin-left: 7px;
                border-bottom-right-radius: 25px;
                border-top-left-radius: 10px;

                &:before {
                    position: absolute;
                    z-index: 1;
                    left: 0;
                    top: 0;
                    width: 26px;
                    height: 100%;
                    border-top-left-radius: 10px;
                    transform: translate(-18px) skew(-25deg);
                    display: block;
                    background-color: #f2f2f6;
                    content: '';
                }
            }

            #{$parent}__ym-wrapper {
                flex-direction: row;
            }

            .divider {
                margin: 0 6px;
            }
        }
    }

    &--interval.date-block--size-xl {
        #{$parent}__date {
            font-size: 64px;
        }

        #{$parent}__icon {
            margin-top: 10px;
        }

        @include breakpoint(mobile) {
            #{$parent}__date {
                font-size: 48px;
            }

            #{$parent}__ym-wrapper {
                font-size: 18px;
            }
        }
    }
}
</style>
